import React, { useState } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';

const ACTION_COLORS = {
    'SYSTEM_CONFIG_UPDATED': { color: 'text-blue-500', bg: 'bg-blue-500' },
    'CLIENT_MAINTENANCE_ENABLED': { color: 'text-orange-500', bg: 'bg-orange-500' },
    'CLIENT_MAINTENANCE_DISABLED': { color: 'text-green-500', bg: 'bg-green-500' },
    'CACHE_CLEARED': { color: 'text-purple-500', bg: 'bg-purple-500' },
    'DATABASE_MIGRATIONS_RUN': { color: 'text-indigo-500', bg: 'bg-indigo-500' },
    'CUSTOMER_IMPERSONATION': { color: 'text-yellow-500', bg: 'bg-yellow-500' },
    'AUDIT_LOGS_CLEANUP': { color: 'text-red-500', bg: 'bg-red-500' }
};

const AuditStatsCards = ({ stats }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    const topActions = Object.entries(stats.actionBreakdown || {})
        .sort(([,a], [,b]) => b - a)
        .slice(0, 4);

    const topAdmins = Object.entries(stats.adminBreakdown || {})
        .sort(([,a], [,b]) => b.count - a.count)
        .slice(0, 3);

    return (
        <div className={`flex flex-col ${isExpanded ? 'gap-4' : ''}`}>
            {/* Collapsible Header */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex flex-row gap-2 items-center justify-between p-2.5 bg-primary-10 border-2 border-primary-50 rounded-md text-font-primary hover:border-primary transition-all duration-200"
            >
                <div className="flex items-center gap-2">
                    <Icon icon="mdi:chart-box" className="text-xl text-primary" />
                    <span className="font-semibold text-sm sm:text-base">Overview</span>
                    <span className="text-xs text-font-secondary">
                        ({stats.totalLogs || 0} logs)
                    </span>
                </div>
                <Icon 
                    icon={isExpanded ? "mingcute:up-line" : "mingcute:down-line"} 
                    className="text-xl text-accent transition-transform duration-300"
                />
            </button>

            {/* Collapsible Content */}
            <div 
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                    isExpanded 
                        ? 'max-h-[2000px] opacity-100' 
                        : 'max-h-0 opacity-0'
                }`}
            >
                <div className="flex flex-col xl:flex-row gap-4">
                    {/* Top Admins */}
                    {topAdmins.length > 0 && (
                        <div className="flex flex-col gap-3 w-full xl:w-1/2">
                            <div className="flex items-center gap-2">
                                <Icon icon="mdi:account-group" className="text-xl text-primary" />
                                <h4 className="font-semibold text-font-primary">Most Active Admins</h4>
                            </div>
                            <div className="flex flex-row flex-wrap gap-3 w-full">
                                {topAdmins.map(([userId, data]) => (
                                    <div key={userId} className="card-container rounded-md flex flex-row items-center gap-2 w-fit py-2 px-3">
                                        <div className="flex items-center gap-1">
                                            <Icon icon="mdi:account-circle" className="text-2xl text-primary" />
                                            <div className="text-xl font-bold text-font-primary">
                                                {data.count}
                                            </div>
                                        </div>
                                        <div className="flex flex-col">
                                            <div className="text-sm font-semibold text-font-primary truncate">
                                                {data.admin_name || data.name || 'Unknown'}
                                            </div>
                                            {(data.admin_email || data.email) && (
                                                <div className="text-xs text-font-secondary truncate">
                                                    {data.admin_email || data.email}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Breakdown */}
                    {topActions.length > 0 && (
                        <div className="flex flex-col gap-3 w-full xl:w-1/2">
                            <div className="flex items-center gap-2">
                                <Icon icon="mdi:chart-bar" className="text-xl text-primary" />
                                <h4 className="font-semibold text-font-primary">Top Actions</h4>
                            </div>
                            <div className="flex flex-row flex-wrap gap-2 w-fit">
                                {topActions.map(([action, count]) => {
                                    const colorConfig = ACTION_COLORS[action] || { 
                                        color: 'text-gray-500', 
                                        bg: 'bg-gray-500' 
                                    };
                                    return (
                                        <div key={action} className="card-container rounded-md flex flex-row items-center gap-2 w-fit py-2 px-3">
                                            <div className="flex items-center gap-2">
                                                <div className={`w-3 h-3 rounded-full ${colorConfig.bg}`} />
                                                <div className="text-xl font-bold text-primary">{count}</div>
                                            </div>
                                            <div className="text-sm font-bold text-font-primary">
                                                {action.replace(/_/g, ' ')}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditStatsCards;

